import { MODAL_UPGRADE_PLAN } from '@components/App/Modal/constants'
import { openLeaveFeedbackModal } from '@components/App/Modal/LeaveFeedback'
import type { BlockWireframe } from '@octopus-do/octopus-project-core'
import {
  toggleWireframe,
  type WireframeVariants,
} from '@octopus-do/octopus-storybook'
import {
  artBoardActions,
  selectWireframesMultiple,
} from '@store/reducers/artboard'
import { openModal } from '@storeOld/reducers/ui'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { isBlockWireframe } from '@/constants/wireframes'
import { useProjectAccess } from '@/hooks'
import { WIREFRAMES_PERMISSION } from '@/permissions/project/definitions'

export const useWireframesMenu = (
  wireframes: BlockWireframe[],
  onChange: (wireframes: BlockWireframe[]) => void
) => {
  const dispatch = useDispatch()

  const single = useProjectAccess(WIREFRAMES_PERMISSION.SINGLE)
  const multiply = useProjectAccess(WIREFRAMES_PERMISSION.MULTIPLY)
  const request = useProjectAccess(WIREFRAMES_PERMISSION.REQUEST_WIREFRAMES)

  const multiple = useSelector(selectWireframesMultiple)

  const onMultipleChange = (next: boolean) => {
    dispatch(artBoardActions.setWireframesMultiple(next))
  }

  // `multiple` is the switch or the Shift key, the menu tells them apart from
  // nothing. The catalog only offers ids of the registry, so the filter drops
  // nothing; it keeps the package's wider variant union out of the project model.
  const onPick = (id: WireframeVariants, multiple: boolean) => {
    const gtmEvent = multiple
      ? GtmTriggerName.WF_MULTIPLE
      : GtmTriggerName.WF_ADD
    sendGtmEvent(gtmEvent, 'Add wireframe to block')

    onChange(toggleWireframe(wireframes, id, multiple).filter(isBlockWireframe))
  }

  const onClear = () => {
    onChange([])
  }

  const onRequest = () => {
    sendGtmEvent(
      GtmTriggerName.WF_REQUEST,
      'Clicked to open leave feedback form to request additional wireframes'
    )
    dispatch(openLeaveFeedbackModal('wireframe'))
  }

  const onUpgrade = () => {
    sendGtmEvent(GtmTriggerName.UPGRADE_WIREFRAMES)
    dispatch(openModal(MODAL_UPGRADE_PLAN))
  }

  return {
    locked: single.hasLimitedAccess || multiply.hasLimitedAccess,
    multiple,
    onClear,
    onMultipleChange,
    onPick,
    onRequest,
    onUpgrade,
    request: request.hasAccess,
  }
}
